import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Avatar,
  Chip,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  LinearProgress,
  TextField,
  InputAdornment,
  Pagination
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Group as GroupIcon,
  Info as InfoIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

// 颜色常量
const COLORS = [
  '#1976d2', // 蓝色
  '#4caf50', // 绿色
  '#ff9800', // 橙色
  '#9c27b0', // 紫色
  '#e91e63'  // 粉色
];

// 生成模拟数据（保持与UwstatisticsPage一致）
const generateBranchData = () => {
  const branches = [
    {
      id: 'all',
      name: 'All Branches',
      location: 'Company Wide',
      monthlyPolicies: 0,
      annualSubmit: 0,
      totalBrokers: 200,
      totalYoungDriver: 0,
      totalSingleAuto: 0,
      totalHighRisk: 0,
      totalHighQuality: 0,
      totalAuto: 0,
      totalHome: 0,
      totalCommercial: 0,
      totalPrem: 0,
      avgQualityScore: 0
    },
    {
      id: 'downtown',
      name: 'Downtown Branch',
      location: 'Vancouver Downtown',
      monthlyPolicies: 220,
      autoSubmit: 132,
      homeSubmit: 66,
      commercialSubmit: 22,
      annualSubmit: 2640,
      totalBrokers: 60
    },
    {
      id: 'richmond',
      name: 'Richmond Branch',
      location: 'Richmond Center',
      monthlyPolicies: 180,
      autoSubmit: 108,
      homeSubmit: 54,
      commercialSubmit: 18,
      annualSubmit: 2160,
      totalBrokers: 50
    },
    {
      id: 'burnaby',
      name: 'Burnaby Branch',
      location: 'Metrotown',
      monthlyPolicies: 170,
      autoSubmit: 102,
      homeSubmit: 51,
      commercialSubmit: 17,
      annualSubmit: 2040,
      totalBrokers: 50
    },
    {
      id: 'surrey',
      name: 'Surrey Branch',
      location: 'Surrey Central',
      monthlyPolicies: 150,
      autoSubmit: 90,
      homeSubmit: 45,
      commercialSubmit: 15,
      annualSubmit: 1800,
      totalBrokers: 40
    }
  ];

  return branches.map(branch => {
    if (branch.id === 'all') {
      return {
        ...branch,
        brokers: [],
        monthlyPolicies: 720,
        annualSubmit: 8640,
        totalYoungDriver: 150,
        totalSingleAuto: 360,
        totalHighRisk: 60,
        totalHighQuality: 280,
        totalAuto: 432,
        totalHome: 216,
        totalCommercial: 72,
        monthlyPrem: 140000,
        avgQualityScore: 96
      };
    }

    // 生成经纪人数据时添加annualSubmit
    const brokers = Array(branch.totalBrokers).fill(null).map((_, index) => {
      const baseMonthly = Math.floor(branch.monthlyPolicies / branch.totalBrokers);
      const monthlyPolicies = baseMonthly + Math.floor(Math.random() * 5);
      
      // 调整各险种保单数的计算方式，确保总和等于monthlyPolicies
      const autoSubmit = Math.floor(monthlyPolicies * 0.60);
      const homeSubmit = Math.floor(monthlyPolicies * 0.30);
      const commercialSubmit = monthlyPolicies - autoSubmit - homeSubmit; // 确保总和正确
      
      return {
        id: `${branch.id}-${index}`,
        name: `Broker ${index + 1}`,
        monthlyPolicies: monthlyPolicies,
        annualSubmit: monthlyPolicies * 12 + Math.floor(Math.random() * 24),
        autoSubmit: autoSubmit,
        homeSubmit: homeSubmit,
        commercialSubmit: commercialSubmit,
        highRiskDriver: Math.floor(monthlyPolicies * 0.08),
        highQualityInsured: Math.floor(monthlyPolicies * 0.40),
        monthlyPrem: (monthlyPolicies * 2333 * (0.9 + Math.random() * 0.3)).toFixed(2),
        youngDriverSubmit: Math.floor(monthlyPolicies * 0.20),
        singleAutoSubmit: Math.floor(monthlyPolicies * 0.50),
        qualityScore: Math.floor(88 + Math.random() * 9),
        branch: branch.name
      };
    });

    // 计算分行汇总数据
    const branchTotal = {
      ...branch,
      brokers,
      annualSubmit: brokers.reduce((sum, b) => sum + b.annualSubmit, 0),
      totalYoungDriver: brokers.reduce((sum, b) => sum + b.youngDriverSubmit, 0),
      totalSingleAuto: brokers.reduce((sum, b) => sum + b.singleAutoSubmit, 0),
      totalHighRisk: brokers.reduce((sum, b) => sum + b.highRiskDriver, 0),
      totalHighQuality: brokers.reduce((sum, b) => sum + b.highQualityInsured, 0),
      totalAuto: brokers.reduce((sum, b) => sum + b.autoSubmit, 0),
      totalHome: brokers.reduce((sum, b) => sum + b.homeSubmit, 0),
      totalCommercial: brokers.reduce((sum, b) => sum + b.commercialSubmit, 0),
      monthlyPrem: brokers.reduce((sum, b) => sum + parseFloat(b.monthlyPrem), 0),
      avgQualityScore: Math.floor(brokers.reduce((sum, b) => sum + b.qualityScore, 0) / brokers.length)
    };

    return branchTotal;
  });
};

const BrokerTable = ({ brokers = [] }) => (
  <TableContainer component={Paper} elevation={0} sx={{ 
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    overflow: 'hidden'
  }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
          <TableCell>Branch</TableCell>
          <TableCell>Broker</TableCell>
          <TableCell align="center" sx={{ fontWeight: 500 }}>Monthly</TableCell>
          <TableCell align="center" sx={{ fontWeight: 500 }}>Annual</TableCell>
          <TableCell align="center">Auto</TableCell>
          <TableCell align="center">Home</TableCell>
          <TableCell align="center">Commercial</TableCell>
          <TableCell align="center">Young</TableCell>
          <TableCell align="center">Single Auto</TableCell>
          <TableCell align="center">High Risk</TableCell>
          <TableCell align="center">High Quality</TableCell>
          <TableCell align="center">Prem($)</TableCell>
          <TableCell align="center">Quality</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {brokers.map((broker) => (
          <TableRow 
            key={broker.id} 
            hover 
            sx={{ 
              '&:hover': { bgcolor: '#f5f9ff' }
            }}
          >
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
                {broker.branch}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {broker.name}
              </Typography>
            </TableCell>
            <TableCell align="center">{broker.monthlyPolicies}</TableCell>
            <TableCell align="center">{broker.annualSubmit}</TableCell>
            <TableCell align="center">{broker.autoSubmit}</TableCell>
            <TableCell align="center">{broker.homeSubmit}</TableCell>
            <TableCell align="center">{broker.commercialSubmit}</TableCell>
            <TableCell align="center">{broker.youngDriverSubmit}</TableCell>
            <TableCell align="center">{broker.singleAutoSubmit}</TableCell>
            <TableCell align="center">
              <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                {broker.highRiskDriver}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                {broker.highQualityInsured}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ${Number(broker.monthlyPrem).toLocaleString()}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="body2" sx={{ 
                fontWeight: 500,
                color: broker.qualityScore >= 90 ? '#2e7d32' :
                        broker.qualityScore >= 80 ? '#ed6c02' : '#d32f2f'
              }}>
                {broker.qualityScore}%
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// 新增分行统计表格组件
const BranchStatsTable = ({ branches }) => (
  <TableContainer component={Paper} elevation={0} sx={{ 
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    overflow: 'hidden'
  }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
          <TableCell>Branch</TableCell>
          <TableCell align="center">Monthly Submit</TableCell>
          <TableCell align="center">Annual Submit</TableCell>
          <TableCell align="center">Auto</TableCell>
          <TableCell align="center">Home</TableCell>
          <TableCell align="center">Commercial</TableCell>
          <TableCell align="center">Monthly Prem($)</TableCell>
          <TableCell align="center">Brokers</TableCell>
          <TableCell align="center">Avg Quality</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {branches.map((branch) => (
          <TableRow 
            key={branch.id} 
            hover
            sx={{ 
              '&:hover': { bgcolor: '#f5f9ff' }
            }}
          >
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
                {branch.name}
              </Typography>
            </TableCell>
            <TableCell align="center">{branch.monthlyPolicies}</TableCell>
            <TableCell align="center">{branch.annualSubmit}</TableCell>
            <TableCell align="center">
              {branch.id === 'all' ? branch.totalAuto : 
                Math.floor(branch.monthlyPolicies * 0.60)}
            </TableCell>
            <TableCell align="center">
              {branch.id === 'all' ? branch.totalHome : 
                Math.floor(branch.monthlyPolicies * 0.30)}
            </TableCell>
            <TableCell align="center">
              {branch.id === 'all' ? branch.totalCommercial : 
                branch.totalCommercial || Math.floor(branch.monthlyPolicies * 0.10)}
            </TableCell>
            <TableCell align="center">
              ${(branch.id === 'all' ? branch.monthlyPrem : 
                branch.monthlyPolicies * 2333).toLocaleString()}
            </TableCell>
            <TableCell align="center">{branch.totalBrokers}</TableCell>
            <TableCell align="center">
              <Typography 
                variant="body2"
                sx={{ 
                  color: 
                    branch.avgQualityScore >= 85 ? '#2e7d32' :
                    branch.avgQualityScore >= 75 ? '#ef6c00' : '#c62828'
                }}
              >
                {branch.avgQualityScore}%
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ITEMS_PER_PAGE = 5; // 每页显示5条记录

const BrokerQualityDashboard = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage] = useState(ITEMS_PER_PAGE);
  const [timeRange, setTimeRange] = useState('monthly'); // 'monthly' or 'annual'

  const branchesData = generateBranchData();
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [expanded, setExpanded] = useState(false);

  const currentBranch = useMemo(() => 
    branchesData.find(b => b.id === selectedBranch) || branchesData[0],
    [selectedBranch, branchesData]
  );

  // 处理搜索和分页
  const filteredBrokers = useMemo(() => {
    if (selectedBranch === 'all') {
      return branchesData
        .filter(b => b.id !== 'all')
        .flatMap(b => 
          (b.brokers || []).map(broker => ({
            ...broker,
            branch: b.name
          }))
        );
    }
    return (branchesData.find(b => b.id === selectedBranch)?.brokers || [])
      .filter(broker => 
        broker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        broker.branch.toLowerCase().includes(searchTerm.toLowerCase())
      );
  }, [branchesData, selectedBranch, searchTerm]);

  const paginatedBrokers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredBrokers.slice(start, start + itemsPerPage);
  }, [currentPage, filteredBrokers, itemsPerPage]);

  // 数据生成函数
  const generateRevenueData = (branches, range) => {
    return branches.filter(b => b.id !== 'all').map(branch => ({
      name: branch.name,
      value: range === 'monthly' ? 
        branch.monthlyPrem * 0.85 : // 假设85%的月保费收入
        branch.monthlyPrem * 12 * 0.82 // 年收入估算
    }));
  };

  const generateAgeData = (branches) => {
    const ageGroups = [
      { name: '18-25', value: 0 },
      { name: '26-35', value: 0 },
      { name: '36-50', value: 0 },
      { name: '51+', value: 0 }
    ];

    branches.filter(b => b.id !== 'all').forEach(branch => {
      branch.brokers.forEach(broker => {
        const age = Math.floor(18 + Math.random() * 40);
        if (age <= 25) ageGroups[0].value += 1;
        else if (age <= 35) ageGroups[1].value += 1;
        else if (age <= 50) ageGroups[2].value += 1;
        else ageGroups[3].value += 1;
      });
    });

    return ageGroups;
  };

  const generateClientTypeData = (branches) => {
    let totalAuto = 0;
    let totalHome = 0;
    let totalCommercial = 0;

    branches.filter(b => b.id !== 'all').forEach(branch => {
      totalAuto += branch.totalAuto;
      totalHome += branch.totalHome;
      totalCommercial += branch.totalCommercial;
    });

    return [
      { name: 'Auto', value: totalAuto },
      { name: 'Home', value: totalHome },
      { name: 'Commercial', value: totalCommercial },
    ];
  };

  // 常量定义
  const KEY_METRICS = [
    { key: 'monthlyPolicies', label: 'Monthly Policies', color: '#1976d2' },
    { key: 'annualSubmit', label: 'Annual Submit', color: '#4caf50' },
    { key: 'monthlyPrem', label: 'Monthly Premium', color: '#ff9800' },
    { key: 'totalBrokers', label: 'Total Brokers', color: '#9c27b0' },
    { key: 'avgQualityScore', label: 'Quality Score', color: '#2196f3' },
    { key: 'totalHighQuality', label: 'High Quality', color: '#4caf50' }
  ];

  // 组件拆分
  const MetricCard = ({ label, value, color }) => (
    <Card sx={{ 
      height: '100%', 
      p: 1.5,
      '&:hover': { 
        transform: 'translateY(-2px)',
        boxShadow: 2,
        transition: 'all 0.2s'
      }
    }}>
      <CardContent sx={{ p: '8px !important' }}>
        <Typography variant="caption" color="textSecondary" gutterBottom>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  const ChartSection = ({ branchesData, timeRange, currentBranch }) => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={4}>
        <RevenueChart 
          data={generateRevenueData(branchesData, timeRange)} 
          colors={COLORS}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <AgeDistributionChart 
          data={generateAgeData(branchesData)} 
          colors={COLORS}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PolicyTypeChart 
          data={generateClientTypeData(branchesData)} 
          colors={COLORS}
        />
      </Grid>
    </Grid>
  );

  const DataTableSection = ({ 
    selectedBranch, 
    branchesData, 
    paginatedBrokers, 
    filteredBrokers,
    currentPage,
    itemsPerPage,
    onPageChange 
  }) => (
    <Box>
      {selectedBranch === 'all' ? (
        <BranchStatsTable 
          branches={branchesData.filter(b => b.id !== 'all')} 
        />
      ) : (
        <>
          <BrokerTable brokers={paginatedBrokers} />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              count={Math.ceil(filteredBrokers.length / itemsPerPage)}
              page={currentPage}
              onChange={(_, value) => onPageChange(value)}
              color="primary"
            />
          </Box>
        </>
      )}
    </Box>
  );

  // 辅助函数
  const getMetricLabel = (metric) => {
    const labels = {
      monthlyPolicies: 'Monthly Policies',
      annualSubmit: 'Annual Submissions',
      monthlyPrem: 'Monthly Premium',
      totalBrokers: 'Total Brokers',
      totalAuto: 'Auto Policies',
      totalHome: 'Home Policies',
      totalCommercial: 'Commercial Policies',
      avgQualityScore: 'Quality Score'
    };
    return labels[metric] || metric;
  };

  const formatMetricValue = (metric, value) => {
    if (metric === 'monthlyPrem') {
      return `$${(value || 0).toLocaleString(undefined, { 
        maximumFractionDigits: 0 
      })}`;
    }
    if (metric === 'avgQualityScore') {
      return `${value || 0}%`;
    }
    return value || 0;
  };

  // 图表组件定义
  const RevenueChart = ({ data, colors }) => (
    <Paper elevation={3} sx={{ p: 3, height: 400 }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
        Revenue Distribution by Branch
      </Typography>
      <ResponsiveContainer width="100%" height="90%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={5}
            dataKey="value"
            label={({ percent }) => `${(percent * 100).toFixed(2)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Legend layout="vertical" verticalAlign="middle" align="right" />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );

  const AgeDistributionChart = ({ data, colors }) => (
    <Paper elevation={3} sx={{ p: 3, height: 400 }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
        Age Distribution of Policy Holders
      </Typography>
      <ResponsiveContainer width="100%" height="90%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={5}
            dataKey="value"
            label={({ percent }) => `${(percent * 100).toFixed(2)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Legend layout="vertical" verticalAlign="middle" align="right" />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );

  const PolicyTypeChart = ({ data, colors }) => (
    <Paper elevation={3} sx={{ p: 3, height: 400 }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
        Policy Type Distribution
      </Typography>
      <ResponsiveContainer width="100%" height="90%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={5}
            dataKey="value"
            label={({ percent }) => `${(percent * 100).toFixed(2)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Legend layout="vertical" verticalAlign="middle" align="right" />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );

  // 修改分支选择处理函数
  const handleBranchChange = (event) => {
    setSelectedBranch(event.target.value);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        {/* 标题部分 */}
        <Typography variant="h4" sx={{ 
          color: '#1976d2', 
          mb: 3,
          fontWeight: 600,
        }}>
          Broker Performance Dashboard
        </Typography>

        {/* 分支选择和时间范围选择器 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <FormControl 
              fullWidth 
              variant="outlined"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                }
              }}
            >
              <InputLabel>Select Branch</InputLabel>
              <Select
                value={selectedBranch}
                onChange={handleBranchChange}
                label="Select Branch"
              >
                {branchesData.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="annual">Annual</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Search brokers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9e9e9e' }} />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>

        {/* 关键指标卡片 - 使用常量定义指标 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {KEY_METRICS.map((metric) => (
            <Grid item xs={6} sm={4} md={2} key={metric.key}>
              <MetricCard 
                label={metric.label}
                value={formatMetricValue(metric.key, currentBranch[metric.key])}
                color={metric.color}
              />
            </Grid>
          ))}
        </Grid>

        {/* 图表部分 */}
        <ChartSection 
          branchesData={branchesData}
          timeRange={timeRange}
          currentBranch={currentBranch}
        />

        {/* 数据表格部分 */}
        <DataTableSection 
          selectedBranch={selectedBranch}
          branchesData={branchesData}
          paginatedBrokers={paginatedBrokers}
          filteredBrokers={filteredBrokers}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </Box>
    </Container>
  );
};

export default BrokerQualityDashboard; 